import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

function fmtCurrency(val) {
  if (val == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(val))
}

function fmtCompact(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 100_000) return `$${(n / 1_000).toFixed(1)}k`
  return fmtCurrency(n)
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const data = payload[0].payload
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-sans text-xs font-semibold text-ink">{data.name}</div>
      <div className="font-mono text-xs text-inkMuted">
        {fmtCurrency(data.value)}{data.pct != null ? ` (${data.pct.toFixed(1)}%)` : ''}
      </div>
    </div>
  )
}

function ConcentrationTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const data = payload[0].payload
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-xs font-semibold text-ink">{data.symbol}</div>
      <div className="font-mono text-xs text-inkMuted">{data.pct.toFixed(2)}% of portfolio</div>
    </div>
  )
}

function DonutChart({ data, centerLabel, centerSub, size = 200 }) {
  const chartData = data.filter(item => Number(item.value) > 0)
  if (!chartData.length) return null
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={size * 0.28}
            outerRadius={size * 0.42}
            paddingAngle={1}
            dataKey="value"
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="font-mono text-sm font-bold text-ink">{centerLabel}</div>
        {centerSub && <div className="font-mono text-[10px] text-inkMuted">{centerSub}</div>}
      </div>
    </div>
  )
}

function ChartCard({ eyebrow, title, children }) {
  return (
    <div className="card">
      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">{eyebrow}</div>
      <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">{title}</h3>
      {children}
    </div>
  )
}

function BreakdownRows({ rows, firstHeader, countHeader = '#' }) {
  return (
    <div className="flex-1 min-w-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">{firstHeader}</th>
            <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Mkt Value</th>
            <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
            <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">{countHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
              <td className="py-2 pr-3 text-left">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: row.color }} />
                  <span className="font-sans text-xs text-ink">{row.name}</span>
                </div>
              </td>
              <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{row.maskedValue}</td>
              <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{row.maskedPct}</td>
              <td className="py-2 text-right font-mono text-xs text-inkMuted">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function PortfolioAllocation({ allocation, mask = value => value }) {
  if (!allocation) return null

  const {
    accountChartData,
    accountValue,
    allConsolidated,
    cashVsInvestedData,
    concentrationData,
    portfolioValue,
    sectorChartData,
    topHoldingsData,
    typeChartData,
  } = allocation

  const isDark = document.documentElement.classList.contains('dark')
  const axisColor = isDark ? '#8a92a0' : '#3d3b34'
  const chartGreen = isDark ? '#6aaa5e' : '#4a6741'
  const chartEmber = isDark ? '#e05a4a' : '#b83a2e'

  const maskCurrency = value => mask(fmtCurrency(value))
  const maskPct = value => mask(value.toFixed(1) + '%')
  const maskedCompactPortfolio = mask(fmtCompact(portfolioValue))
  const maskedCompactAccountValue = mask(fmtCompact(accountValue))

  return (
    <div>
      <div className="mb-4">
        <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Analytics</div>
        <h2 className="font-black text-base uppercase tracking-wide text-ink">Portfolio Allocation</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard eyebrow="Breakdown" title="By Instrument Type">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <DonutChart data={typeChartData} centerLabel={maskedCompactPortfolio} centerSub="Portfolio" />
            </div>
            <BreakdownRows
              firstHeader="Type"
              rows={typeChartData.map(row => ({
                ...row,
                maskedValue: maskCurrency(row.value),
                maskedPct: maskPct(row.pct),
              }))}
            />
          </div>
        </ChartCard>

        <ChartCard eyebrow="Concentration" title="Top Holdings">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <DonutChart data={topHoldingsData} centerLabel={`${allConsolidated.length}`} centerSub="Positions" />
            </div>
            <BreakdownRows
              firstHeader="Symbol"
              countHeader="Accts"
              rows={topHoldingsData.map(row => ({
                ...row,
                count: row.acctCount,
                maskedValue: maskCurrency(row.value),
                maskedPct: maskPct(row.pct),
              }))}
            />
          </div>
        </ChartCard>

        <ChartCard eyebrow="Distribution" title="By Account">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <DonutChart
                data={accountChartData}
                centerLabel={maskedCompactAccountValue}
                centerSub={`${accountChartData.length} Acct${accountChartData.length !== 1 ? 's' : ''}`}
              />
            </div>
            <BreakdownRows
              firstHeader="Account"
              countHeader="Pos"
              rows={accountChartData.map(row => ({
                ...row,
                count: row.posCount,
                maskedValue: maskCurrency(row.value),
                maskedPct: maskPct(row.pct),
              }))}
            />
          </div>
        </ChartCard>

        <ChartCard eyebrow="Risk" title="Concentration Risk">
          {concentrationData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(concentrationData.length * 28 + 40, 120)}>
              <BarChart data={concentrationData} layout="vertical" margin={{ top: 5, right: 50, left: 5, bottom: 5 }}>
                <XAxis
                  type="number"
                  domain={[0, Math.max(...concentrationData.map(row => row.pct), 12)]}
                  tick={{ fill: axisColor, fontSize: 10, fontFamily: '"DM Sans", sans-serif' }}
                  tickFormatter={value => value.toFixed(0) + '%'}
                  axisLine={{ stroke: 'rgb(var(--color-border))' }}
                  tickLine={{ stroke: 'rgb(var(--color-border))' }}
                />
                <YAxis
                  type="category"
                  dataKey="symbol"
                  width={55}
                  tick={{ fill: axisColor, fontSize: 11, fontFamily: '"DM Sans", sans-serif' }}
                  axisLine={{ stroke: 'rgb(var(--color-border))' }}
                  tickLine={false}
                />
                <ReferenceLine
                  x={10}
                  stroke={chartEmber}
                  strokeDasharray="4 4"
                  label={{ value: '10%', position: 'top', fill: chartEmber, fontSize: 10, fontFamily: '"DM Sans", sans-serif' }}
                />
                <Tooltip content={<ConcentrationTooltip />} />
                <Bar dataKey="pct" radius={[0, 3, 3, 0]} barSize={16}>
                  {concentrationData.map((entry, index) => (
                    <Cell key={index} fill={entry.pct > 10 ? chartEmber : chartGreen} />
                  ))}
                  <LabelList
                    dataKey="pctLabel"
                    position="right"
                    style={{ fill: axisColor, fontSize: 10, fontFamily: '"DM Sans", sans-serif' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 font-mono text-xs text-inkMuted">No position data</div>
          )}
        </ChartCard>

        <ChartCard eyebrow="Diversification" title="Sector Allocation">
          {sectorChartData.length > 0 ? (
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <DonutChart data={sectorChartData} centerLabel={`${sectorChartData.length}`} centerSub="Sectors" />
              </div>
              <BreakdownRows
                firstHeader="Sector"
                rows={sectorChartData.map(row => ({
                  ...row,
                  maskedValue: maskCurrency(row.value),
                  maskedPct: maskPct(row.pct),
                }))}
              />
            </div>
          ) : (
            <div className="text-center py-8 font-mono text-xs text-inkMuted">No position data</div>
          )}
        </ChartCard>

        <ChartCard eyebrow="Liquidity" title="Cash vs Invested">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex-shrink-0">
              <DonutChart data={cashVsInvestedData} centerLabel={maskedCompactAccountValue} centerSub="Total" />
            </div>
            <div className="flex-1 min-w-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">Category</th>
                    <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Amount</th>
                    <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
                  </tr>
                </thead>
                <tbody>
                  {cashVsInvestedData.map((row, index) => (
                    <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
                      <td className="py-2 pr-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: row.color }} />
                          <span className="font-sans text-xs text-ink">{row.name}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{maskCurrency(row.value)}</td>
                      <td className="py-2 text-right font-mono text-xs text-inkMuted">{maskPct(row.pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  )
}
